import React, { useState, useEffect, useMemo } from "react";
import MainBG from "../../../assets/MainBG.png";
import logo from "../../../assets/logosss.png";
import { P } from "../../../components/Common/Typo/typo";
import { Card, CardTip, SubCard } from "./Card";
import { LineChart, mockData } from "../../../components/charts/line";
import EnergyUsageCard from "./EnergyUsageCard";
import Electricity from "../../../assets/icons/electric.svg";
import Earth from "../../../assets/earth.svg";
import Lighting from "../../../assets/icons/lighting.svg";
import Hvac from "../../../assets/icons/hvac.svg";
import pic1 from "../../../assets/overview/1.png";
import pic2 from "../../../assets/overview/2.png";
import pic3 from "../../../assets/overview/3.png";
import pic4 from "../../../assets/overview/4.png";
import Trees from "../../../assets/trees.svg";
import useSWR from "swr";
import dayjs from "dayjs";
import { formatUnixInAppTz } from "../../../utils/datetime";
import useFirebase from "../../../utils/hooks/useFirebase";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import "dayjs/locale/th";

const CHART_HEIGHT = 300
const CHART_TOP_PADDING = 40
const CHART_BOTTOM_PADDING = 80
const CHART_CONTENT_HEIGHT = CHART_HEIGHT - CHART_TOP_PADDING - CHART_BOTTOM_PADDING
const CHART_LINE_GAP = 6

const OverviewDashboard = () => {
  const [co2CardData, setCo2CardData] = useState([]);
  const [iaqCardData, setIaqCardData] = useState([]);
  const [energyUsageCardData, setEnergyUsageCardData] = useState([]);
  const [recycleCardData, setRecycleCardData] = useState([]);
  const { dataFirebase } = useFirebase();

  const { data: co2Data } = useSWR([
    "/v1/thaihealth/dashboard_data/co2_emission/",
    { month: dayjs().format("MM-YYYY") },
  ]);
  const { data: energyUsageData } = useSWR([
    "/v1/thaihealth/dashboard_data/energy_usage/",
    { month: dayjs().format("MM-YYYY") },
  ]);
  const { data: iaqData } = useSWR(["/v1/thaihealth/air_quality/dashboard/"]);
  const { data: recycleData } = useSWR([
    "/v1/thaihealth/graph_data/recycling_management/",
    { year: dayjs().subtract(1, "month").format("YYYY") },
  ]);

  const recycleTarget = recycleCardData[0]?.["values"]?.[0]?.["y"] ?? 0
  const recycleTargetPosition = useMemo(() => {
    if (recycleCardData.length === 0) {
      return 0;
    }
    let max =  Math.max(
      ...(recycleCardData[0]['values'] as any[]).map((val: any) => val.y),
      ...(recycleCardData[1]['values'] as any[]).map((val: any) => val.y)
    );

    max = Math.ceil(max / 5) * 5;
    return CHART_TOP_PADDING + Math.round((1 - recycleTarget / max) * (CHART_CONTENT_HEIGHT)) - CHART_LINE_GAP
  }, [recycleCardData, recycleTarget]);


  useEffect(() => {
    const data: any = [
      {
        title: "อัตราการปล่อยคาร์บอน",
        value: co2Data?.co2_emission_save?.percent
          ? Math.abs(co2Data?.co2_emission_save?.percent)
          : 0,
        unit: "%",
        description: "เปรียบเทียบกับเดือนที่ผ่านมา",
        prefix:
          co2Data?.co2_emission_save?.percent > 100 ? "เพิ่มขึ้น" : "ลดลง",
        image: Earth,
        estimate: co2Data?.co2_emission_save?.value,
        suffix:
          co2Data?.co2_emission_save?.percent > 100 ? (
            <FaArrowUp className="fill-[#EF4337]" />
          ) : (
            <FaArrowDown className="fill-[#43A452] h-[32px]" />
          ),
      },
      {
        title: "เทียบเท่ากับการปลูกต้นไม้ทดแทน",
        value: co2Data?.eq_trees_planted?.value
          ? Math.abs(co2Data?.eq_trees_planted?.value)
          : 0,
        unit: "ต้น",
        description: (
          <div>
            <p>เปรียบเทียบกับเดือนที่ผ่านมา</p>
            <p>
              ใน 1 ปี ต้นไม้สามารถดูดซับคาร์บอนได้{" "}
              {co2Data?.eq_trees_planted?.factor ?? 0} kgCO₂e / ต้น
            </p>
          </div>
        ),
        prefix:
          co2Data?.co2_emission_save?.percent > 100 ? "เพิ่มขึ้น" : "ลดลง",
        suffix:
          co2Data?.co2_emission_save?.percent > 100 ? (
            <FaArrowUp className="fill-[#EF4337]" />
          ) : (
            <FaArrowDown className="fill-[#43A452] h-[32px]" />
          ),
        image: Trees,
      },
    ];
    setCo2CardData(data);
  }, [co2Data]);

  useEffect(() => {
    const data: any = [
      {
        title: "สภาพอากาศวันนี้",
        value: dataFirebase?.["temperature"]?.[0],
        unit: "°C",
        icon_code: dataFirebase?.["icon_code"]?.[0],
      },
      {
        title: "PM 2.5",
        value: iaqData?.pm25,
        unit: "µg/m³",
        status: iaqData?.pm25_status,
      },
      {
        title: "PM 10",
        value: iaqData?.pm10,
        unit: "µg/m³",
        status: iaqData?.pm10_status,
      },
      {
        title: "CO₂",
        value: iaqData?.co2,
        unit: "ppm",
        status: iaqData?.co2_status,
      },

      {
        title: "ความชื้น",
        value: iaqData?.rh,
        unit: "%",
        status: iaqData?.rh_status,
      },
      {
        title: "สารประกอบอินทรีย์ระเหย",
        value: iaqData?.tvoc,
        unit: "ppb",
        status: iaqData?.tvoc_status,
      },
    ];
    setIaqCardData(data);
  }, [iaqData, dataFirebase]);

  const displayedLeftItems: any = iaqCardData?.slice(0, 3);
  const displayedRightItems: any = iaqCardData?.slice(3, 6);

  useEffect(() => {
    const data: any = [
      {
        title: "ระบบไฟฟ้าอื่นๆ",
        usage: energyUsageData?.other?.value,
        percentage: energyUsageData?.other?.ratio,
        icon: energyUsageIcons["other"],
      },
      {
        title: "ระบบแสงสว่าง",
        usage: energyUsageData?.lighting?.value,
        percentage: energyUsageData?.lighting?.ratio,
        icon: energyUsageIcons["lighting"],
      },
      {
        title: "ระบบปรับอากาศ",
        usage: energyUsageData?.hvac?.value,
        percentage: energyUsageData?.hvac?.ratio,
        icon: energyUsageIcons["hvac"],
      },
    ];
    setEnergyUsageCardData(data);
  }, [energyUsageData]);

  useEffect(() => {
    const convertRecycleDataToMockData = (data: Record<string, number>) => {
      const formattedData = Object.entries(data).map(([name, yValue]) => {
        return {
          name: name,
          values: Object.entries(yValue).map(([x, y]) => {
            const timestamp = formatUnixInAppTz(Number(x), "MMM").toLowerCase();
            return { x: timestamp, y };
          }),
        };
      });
      return formattedData;
    };

    if (recycleData) {
      const formattedRecycleData: any =
        convertRecycleDataToMockData(recycleData);
      setRecycleCardData(formattedRecycleData);
    }
  }, [recycleData]);

  const mapPic = [
    {
      title: "หลังคาเขียว รักษ์โลก",
      src: pic1,
      top: 19,
      left: 7,
      image_position: "50% 70%",
    },
    {
      title: "พลังงานแสงอาทิตย์",
      src: pic2,
      top: 3,
      left: 58,
      image_position: "50% 10%",
    },
    {
      title: "ศิลปะจากแรงบันดาลใจ",
      src: pic3,
      top: 63,
      left: 27,
      image_position: "50% 50%",
    },
    {
      title: "พื้นที่แห่งการปฏิสัมพันธ์",
      src: pic4,
      top: 63,
      left: 59,
      image_position: "50% 10%",
    },
  ];

  const energyUsageIcons = {
    other: Electricity,
    lighting: Lighting,
    hvac: Hvac,
  };

  dayjs.locale("th");
  const todayDateThai = dayjs().format("MMMM YYYY");
  dayjs.locale("en");

  return (
    <div
      className="h-full w-full relative"
      style={{
        background:
          // "radial-gradient(331.49% 141.42% at 0% 0%, #ECFAFF 0%, #FFF 19.53%, #FEF7EE 45%, #F9F9F9 61%, #FFF 79.42%, #F4FFF0 100%)",
          "#fef0dd",
      }}
    >
      <div className="absolute bg-backgroundGradient opacity-20 h-full w-full" />
      <div className="h-full flex justify-center items-center">
        <img src={MainBG} alt="Overview" className="h-full" />
      </div>
      {mapPic.map((item) => {
        return (
          <div
            className={`absolute w-[200px] h-fit bg-white p-[6px] rounded-[10px] shadow-lg flex flex-col gap-[4px]`}
            style={{ top: `${item.top}%`, left: `${item.left}%` }}
          >
            <P fontSize="24px" color="#1D1D1C">
              {item.title}
            </P>
            <img
              src={item.src}
              alt="Overview"
              className="h-[60px] rounded-md object-cover"
              style={{ objectPosition: item.image_position }}
            />
          </div>
        );
      })}
      <div className="absolute top-0 left-0 px-8 py-4">
        <div className="flex gap-[12px]">
          <img src={logo} alt="Overview" className="h-[84px]" />
          <div className="flex flex-col justify-center">
            <P fontSize="32px" color="#1D1D1C" fontWeight="700">
              อาคารศูนย์เรียนรู้สุขภาวะ
            </P>
            <P
              fontSize="32px"
              color="#1D1D1C"
              fontWeight="700"
              className="-mt-4"
            >
              สำนักงานกองทุนสนับสนุนการสร้างเสริมสุขภาพ
            </P>
          </div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 px-8 py-4 w-full flex items-center gap-[14px]">
        <div className="w-[50%]">
          <CardTip
            title="การคำนวณการปล่อยคาร์บอน"
            value={
              <>
                CO₂ จากการใช้พลังงาน
                <br />
                = ตัวคูณการปล่อย CO₂
                <br />
                (0.4999 kgCO₂e/kWh)
                <br />x kWh ของพลังงานที่ใช้ไป
              </>
            }
            unit={
              <>
                CO₂ จากขยะมูลฝอย
                <br />
                = ตัวคูณการปล่อย CO₂
                <br />
                (2.3200 kgCO₂e/kg)
                <br />x kg ของขยะมูลฝอยทั่วไปที่เกิดขึ้น
              </>
            }
          />
        </div>
        <div className="flex gap-[6px] w-full">
          {iaqCardData.map((data: any, index: any) => (
            <SubCard
              key={index}
              title={data.title}
              value={data.value}
              status={data.status}
              unit={data.unit}
              isBold={true}
              iconCode={data.icon_code}
            />
          ))}
        </div>
        <div className="w-[50%]">
          <CardTip
            title="รู้หรือไม่ ? ทำไมต้องรีไซเคิล"
            value={
              <>
                การรีไซเคิลช่วยแปรรูปขยะให้กลับมาใช้ใหม่ได้ ประหยัดพลังงาน
                <br />
                อนุรักษ์ทรัพยากร และลดมลพิษ ดีกว่าการฝังกลบ เพียงแค่คุณแยก
                <br />
                ขยะก่อนทิ้ง ก็ช่วยโลกได้แล้ว
                มาเริ่มกันวันนี้เพื่อโลกที่น่าอยู่ขึ้น
              </>
            }
          />
        </div>
      </div>
      <div className="absolute bottom-36 left-0 pl-8 py-4 w-[50%]">
        <div className="flex items-center justify-between w-[50%]">
          <P fontSize="30px" color="#1D1D1C" fontWeight="700">
            การปล่อยคาร์บอน
          </P>
          <div className="border px-[8px] py-[4px] bg-[#EFEFEF] rounded-md h-6 flex items-center justify-center">
            <P fontSize="20px" color="#6F6F6E" fontWeight="700">
              {todayDateThai}
            </P>
          </div>
        </div>
        <div className="w-[50%] flex flex-col gap-[6px]">
          {co2CardData.map((card: any, index: any) => (
            <Card
              key={index}
              title={card.title}
              value={card.value}
              unit={card.unit}
              description={card.description}
              image={card.image}
              estimate={card.estimate}
              prefix={card.prefix}
              suffix={card.suffix}
            />
          ))}
        </div>
      </div>
      <div className="absolute top-0 right-0 px-8 py-4 w-[27%]">
        <div className="flex items-center justify-between">
          <P fontSize="30px" color="#1D1D1C">
            การใช้พลังงาน
          </P>
          <div className="border px-[8px] py-[4px] bg-[#EFEFEF] rounded-md h-6 flex items-center justify-center">
            <P fontSize="20px" color="#6F6F6E">
              {todayDateThai}
            </P>
          </div>
        </div>
        <div className="flex w-full gap-2 my-3">
          {energyUsageCardData.map((card: any, index: any) => (
            <EnergyUsageCard
              key={index}
              title={card.title}
              usage={card.usage}
              percentage={card.percentage}
              icon={card.icon}
            />
          ))}
        </div>
        <CardTip
          title="kWh: หน่วยพลังงานทางไฟฟ้า"
          value={
            <>
              กิโลวัตต์-ชั่วโมง (kWh) คือหน่วยวัดการใช้ไฟฟ้า
              แสดงถึงพลังงานที่ใช้เมื่อเปิดอุปกรณ์
              <br />
              1,000 วัตต์นาน 1 ชั่วโมง เช่น หลอดไฟ 100 วัตต์ เปิด 10 ชั่วโมง
              จะใช้ไฟไป 1 kWh
            </>
          }
        />
      </div>
      <div className="absolute bottom-32 right-0 pr-8 py-4 w-[50%] flex flex-col items-end">
        <div className="flex items-center justify-between w-[50%] -mb-10">
          <P fontSize="30px" color="#1D1D1C">
            อัตราขยะที่ถูกรีไซเคิลแล้ว
          </P>
          <div className="border px-[8px] py-[4px] bg-[#EFEFEF] rounded-md h-8 flex items-center justify-center">
            <P fontSize="20px" color="#6F6F6E" fontWeight="700">
              Unit: % ต่อขยะทั้งหมด
            </P>
          </div>
        </div>
        <div className="w-[50%]">
          <div className="relative">
            <LineChart
              data={recycleCardData}
              title=""
              yAxisName=""
              height={`${CHART_HEIGHT}px`}
            />
            <p className="absolute right-4 text-[16px]" style={{ top: recycleTargetPosition }}>
              {recycleTarget} %
            </p>
          </div>

          <div className="flex items-center justify-center gap-2 -mt-10">
            <div className="flex items-center">
              <div className="w-2.5 h-2.5 bg-green-500 mr-1"></div>
              <P className="text-base font-bold text-gray-700">
                อัตราขยะที่ถูกรีไซเคิล
              </P>
            </div>
            <div className="flex items-center">
              <div className="w-2.5 h-2.5 bg-amber-500 mr-1"></div>
              <P className="text-base font-bold text-gray-700">เป้าหมาย</P>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewDashboard;
